import { useState, type ReactElement } from 'react';
import { ActionIcon, Button, Checkbox, Group, Popover, Stack, Text, Tooltip } from '@mantine/core';
import { Columns3, GripVertical, Pin, PinOff } from 'lucide-react';
import type { MegaGridColumn } from './MegaGridTypes';

interface MegaGridColumnSelectorProps<T> {
    columns: MegaGridColumn<T>[];
    order: string[];
    hidden: string[];
    pinned: string[];
    allowPinning: boolean;
    onToggleVisible: (key: string, visible: boolean) => void;
    onReorder: (order: string[]) => void;
    onTogglePin: (key: string) => void;
}

export default function MegaGridColumnSelector<T>(props: MegaGridColumnSelectorProps<T>): ReactElement {
    const { columns, order, hidden, pinned, allowPinning, onToggleVisible, onReorder, onTogglePin } = props;
    const [dragKey, setDragKey] = useState<string | null>(null);

    const byKey = new Map(columns.map((c) => [c.key, c]));
    const ordered = order.map((k) => byKey.get(k)).filter((c): c is MegaGridColumn<T> => Boolean(c));
    const selectable = ordered.filter((c) => c.hideable !== false);

    const handleDrop = (targetKey: string): void => {
        if (!dragKey || dragKey === targetKey) return;
        const next = [...order];
        const from = next.indexOf(dragKey);
        const to = next.indexOf(targetKey);
        if (from < 0 || to < 0) return;
        next.splice(from, 1);
        next.splice(to, 0, dragKey);
        onReorder(next);
        setDragKey(null);
    };

    return (
        <Popover position="bottom-end" withArrow shadow="md" width={260}>
            <Popover.Target>
                <Button size="xs" variant="default" leftSection={<Columns3 size={14} />}>
                    Columns
                </Button>
            </Popover.Target>
            <Popover.Dropdown>
                <Stack gap={2} mah={360} style={{ overflowY: 'auto' }}>
                    <Group justify="space-between" mb={4}>
                        <Text size="xs" fw={700} tt="uppercase" c="dimmed">
                            Columns
                        </Text>
                        <Button size="compact-xs" variant="subtle" color="gray" onClick={() => selectable.forEach((c) => onToggleVisible(c.key, true))}>
                            Show all
                        </Button>
                    </Group>
                    {ordered.map((col) => {
                        const reorderable = col.reorderable !== false;
                        return (
                            <Group
                                key={col.key}
                                gap={6}
                                wrap="nowrap"
                                px={4}
                                py={3}
                                draggable={reorderable}
                                onDragStart={() => reorderable && setDragKey(col.key)}
                                onDragOver={(e) => e.preventDefault()}
                                onDrop={() => handleDrop(col.key)}
                                style={{
                                    borderRadius: 'var(--mantine-radius-sm)',
                                    background: dragKey === col.key ? 'var(--bm-color-zinc-hover)' : undefined,
                                    cursor: reorderable ? 'grab' : 'default',
                                }}
                            >
                                <GripVertical size={13} opacity={reorderable ? 0.5 : 0.15} />
                                <Checkbox
                                    size="xs"
                                    checked={!hidden.includes(col.key)}
                                    disabled={col.hideable === false}
                                    onChange={(e) => onToggleVisible(col.key, e.currentTarget.checked)}
                                    label={col.header}
                                    style={{ flex: 1 }}
                                />
                                {allowPinning && col.pinnable && (
                                    <Tooltip label={pinned.includes(col.key) ? 'Unpin' : 'Pin left'} withArrow>
                                        <ActionIcon
                                            size="sm"
                                            variant={pinned.includes(col.key) ? 'light' : 'subtle'}
                                            color={pinned.includes(col.key) ? 'brand' : 'gray'}
                                            onClick={() => onTogglePin(col.key)}
                                            aria-label="Pin column"
                                        >
                                            {pinned.includes(col.key) ? <PinOff size={12} /> : <Pin size={12} />}
                                        </ActionIcon>
                                    </Tooltip>
                                )}
                            </Group>
                        );
                    })}
                </Stack>
            </Popover.Dropdown>
        </Popover>
    );
}
